import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { io } from "socket.io-client";
import { format } from "date-fns";
import ApiClient from "../../methods/api/apiClient";
import environment from "../../environment";
import {
  Bell,
  CheckCheck,
  Clock,
  ArrowRight,
  ExternalLink,
  FileText,
  Truck,
  Receipt,
  Sparkles,
  X,
  ShieldAlert,
  ChevronRight,
  Inbox,
} from "lucide-react";

const SOCKET_URL = environment.api || "http://localhost:5000";

function timeAgo(dateStr) {
  if (!dateStr) return "recently";
  const diff = Date.now() - new Date(dateStr).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return format(new Date(dateStr), "MMM dd, yyyy");
}

function getRedirectPath(notif) {
  const meta = notif?.meta;
  if (!notif?.sourceId) return null;

  const id = notif.sourceId;
  const date = Date.now();

  const typeToPath = {
    "generate-quote": `/estimate/detail/${id}?date=${date}`,
    "estimated-counter-offer": `/estimate/detail/${id}?date=${date}`,
    "reject-counter-offer": `/estimate/detail/${id}?date=${date}`,
    "invoice-created": `/invoice/detail/${id}?date=${date}`,
    "shipment-update": `/order/tracking/detail/${id}?date=${date}`,
    "shipment-date-update": `/order/tracking/detail/${id}?date=${date}`,
    "tracking-url": `/order/tracking/detail/${id}?date=${date}`,
    "shipment-with-due": `/order/tracking/detail/${id}?date=${date}`,
    "order-delivered": `/order/tracking/detail/${id}?date=${date}`,
    "payment-completed": `/invoice/detail/${id}?date=${date}`,
  };

  return typeToPath[meta?.type] ?? null;
}

function getNotificationIcon(type) {
  switch (type) {
    case "generate-quote":
    case "estimated-counter-offer":
    case "reject-counter-offer":
      return {
        icon: FileText,
        bg: "bg-blue-50 text-[#115B9D] border border-blue-100",
      };
    case "invoice-created":
    case "payment-completed":
      return {
        icon: Receipt,
        bg: "bg-emerald-50 text-emerald-600 border border-emerald-100",
      };
    case "shipment-update":
    case "shipment-date-update":
    case "tracking-url":
    case "shipment-with-due":
    case "order-delivered":
      return {
        icon: Truck,
        bg: "bg-indigo-50 text-indigo-600 border border-indigo-100",
      };
    default:
      return {
        icon: Sparkles,
        bg: "bg-amber-50 text-amber-600 border border-amber-100",
      };
  }
}

function NotificationModal({ notif, onClose, onMarkRead }) {
  if (!notif) return null;

  const handleModalClick = (e) => e.stopPropagation();

  const handleMarkRead = () => {
    if (!notif.isRead) onMarkRead(notif._id);
    onClose();
  };

  const senderName =
    notif.sendBy?.fullName ||
    notif.senderDetails?.fullName ||
    (notif.sendBy?.firstName
      ? `${notif.sendBy.firstName} ${notif.sendBy.lastName || ""}`.trim()
      : "") ||
    "System Notification";

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[10000] p-4 animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-100 flex flex-col max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-200"
        onClick={handleModalClick}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 bg-gradient-to-b from-slate-50/80 to-white shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 text-[#115B9D] flex items-center justify-center shrink-0 border border-blue-100">
              <Bell size={20} />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">
                Notification Details
              </h3>
              <div className="flex items-center gap-2 mt-0.5">
                {!notif.isRead ? (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                    Unread
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                    Read
                  </span>
                )}
                <span className="text-[11px] text-slate-400">
                  {timeAgo(notif.createdAt)}
                </span>
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1 space-y-4">
          <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100">
            <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
              Title
            </label>
            <p className="text-sm font-semibold text-slate-800 mt-0.5 break-words">
              {notif.title || "Notification"}
            </p>
          </div>

          <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100">
            <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
              Message
            </label>
            <p className="text-sm text-slate-700 mt-0.5 leading-relaxed break-words whitespace-pre-wrap">
              {notif.message}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100">
              <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                Sent By
              </label>
              <div className="flex items-center gap-2 mt-1">
                {notif.sendBy?.image ? (
                  <img
                    src={notif.sendBy.image}
                    alt={senderName}
                    className="w-7 h-7 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-blue-100 text-[#115B9D] font-bold text-xs flex items-center justify-center">
                    {senderName[0]?.toUpperCase() || "S"}
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-slate-800 truncate">
                    {senderName}
                  </p>
                  {notif.sendBy?.email && (
                    <p className="text-[10px] text-slate-400 truncate">
                      {notif.sendBy.email}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100">
              <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                Received At
              </label>
              <p className="text-xs font-medium text-slate-700 mt-1">
                {notif.createdAt
                  ? format(new Date(notif.createdAt), "PPP 'at' h:mm a")
                  : "N/A"}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50/50 shrink-0">
          <button
            onClick={handleMarkRead}
            className={
              !notif.isRead
                ? "inline-flex items-center gap-2 bg-[#115B9D] hover:bg-[#0e4b82] text-white text-xs sm:text-sm rounded-xl transition-all font-semibold px-5 py-2.5 shadow-sm"
                : "inline-flex items-center gap-2 text-xs sm:text-sm text-slate-600 hover:text-slate-800 rounded-xl transition-all border border-slate-200 bg-white font-medium px-5 py-2.5 shadow-sm hover:bg-slate-50"
            }
          >
            {!notif.isRead ? "Mark as Read & Close" : "Close"}
          </button>
        </div>
      </div>
    </div>
  );
}

function NotificationRow({ notif, onRead, onOpenModal, onNavigate }) {
  const meta = notif?.meta;
  const type = meta?.type;
  const { icon: TypeIcon, bg } = getNotificationIcon(type);

  const handleClick = () => {
    if (!notif?.isRead) onRead(notif?._id);

    const path = getRedirectPath(notif);
    if (path) {
      onNavigate(path);
    } else {
      onNavigate("/orders");
    }
  };

  const senderName =
    notif.sendBy?.fullName ||
    notif.senderDetails?.fullName ||
    (notif.sendBy?.firstName
      ? `${notif.sendBy.firstName} ${notif.sendBy.lastName || ""}`.trim()
      : "") ||
    "System";

  return (
    <div
      onClick={handleClick}
      className={`group flex items-start gap-3 px-4 py-3 cursor-pointer transition-all duration-150 relative border-b border-slate-50 last:border-b-0 ${
        notif.isRead
          ? "hover:bg-slate-50/80 bg-white"
          : "bg-blue-50/40 hover:bg-blue-50/70"
      }`}
    >
      {/* Icon with status dot */}
      <div className="relative shrink-0 mt-0.5">
        <div
          className={`w-8 h-8 rounded-xl flex items-center justify-center transition-transform group-hover:scale-105 ${bg}`}
        >
          <TypeIcon size={16} />
        </div>
        {!notif.isRead && (
          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-[#115B9D] rounded-full ring-2 ring-white" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p
            className={`text-xs truncate ${
              notif.isRead
                ? "font-medium text-slate-700"
                : "font-semibold text-slate-900"
            }`}
          >
            {notif.title || "Notification"}
          </p>
          <span className="text-[11px] text-slate-400 shrink-0 font-medium">
            {timeAgo(notif.createdAt)}
          </span>
        </div>

        <p className="text-xs text-slate-500 line-clamp-2 mt-0.5 leading-relaxed">
          {notif.message}
        </p>

        <div className="flex items-center justify-between mt-1.5 pt-1 border-t border-slate-100/60">
          <span className="text-[10px] text-slate-400 flex items-center gap-1 font-medium capitalize">
            From: <span className="text-slate-600 font-semibold">{senderName}</span>
          </span>
          <span className="text-[10px] text-[#115B9D] font-medium opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
            View <ChevronRight size={11} />
          </span>
        </div>
      </div>
    </div>
  );
}

export default function NotificationBell({ colour }) {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const userId = user?._id || user?.id;

  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  const [initialFetchDone, setInitialFetchDone] = useState(false);

  const socketRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchNotifications = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await ApiClient.get(
        `notifications/list?isRead=false&page=1&limit=200000`,
      );
      if (res?.success) {
        const newData = res.data || [];
        setNotifications(newData);
        setTotalUnread(newData.filter((n) => !n.isRead).length);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const markReadAPI = async (id) => {
    try {
      await ApiClient.put(`notifications/update`, { isRead: true, id });
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllReadAPI = async () => {
    try {
      await ApiClient.post(`notifications/mark-all-read`, { userId });
      setNotifications([]);
      setTotalUnread(0);
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  useEffect(() => {
    if (!userId) return;

    if (!initialFetchDone) {
      fetchNotifications();
      setInitialFetchDone(true);
    }

    const socket = io(`${SOCKET_URL}?userId=${userId}`, {
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: 5,
    });

    socketRef.current = socket;

    socket.on("connect", () =>
      console.log("Socket connected for notifications"),
    );

    socket.on("notification", (newNotification) => {
      setNotifications((prev) => [newNotification, ...prev]);
      if (!newNotification.isRead) setTotalUnread((prev) => prev + 1);
    });

    socket.on("connect_error", (error) =>
      console.error("Socket connection error:", error),
    );

    return () => {
      if (socket) socket.disconnect();
    };
  }, [userId, initialFetchDone, fetchNotifications]);

  const handleMarkRead = useCallback(async (id) => {
    setNotifications((prev) =>
      prev.map((n) => (n?._id === id ? { ...n, isRead: true } : n)),
    );
    setTotalUnread((prev) => Math.max(0, prev - 1));
    await markReadAPI(id);
  }, []);

  const handleMarkAllRead = async () => {
    await markAllReadAPI();
  };

  const handleOpenModal = (notification) => {
    setSelectedNotification(notification);
    if (!notification.isRead) handleMarkRead(notification._id);
  };

  const handleCloseModal = () => {
    setSelectedNotification(null);
    setOpen(false);
  };

  const handleNavigate = (path) => {
    setOpen(false);
    navigate(path);
  };

  const handleToggleDropdown = () => {
    setOpen((prev) => !prev);
    if (!open) fetchNotifications();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleToggleDropdown}
        className={`relative flex items-center justify-center w-10 h-10 rounded-full border transition-all duration-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#115B9D]/20 active:scale-95 group ${
          open
            ? "border-[#115B9D]/50 bg-blue-50/70 text-[#115B9D] shadow-md ring-2 ring-[#115B9D]/15"
            : colour?.includes("white")
            ? "border-white/20 bg-white/10 hover:bg-white/20 text-white"
            : "border-slate-200/90 bg-white hover:bg-slate-50 hover:border-slate-300 text-slate-600 hover:text-[#115B9D]"
        }`}
        aria-label="View notifications"
      >
        <Bell
          size={18}
          className="transition-transform duration-200 group-hover:scale-110"
        />
        {totalUnread > 0 && (
          <span className="absolute -top-1 -right-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-gradient-to-r from-red-500 to-rose-600 px-1 text-[10px] font-bold text-white shadow-sm ring-2 ring-white">
            {totalUnread > 99 ? "99+" : totalUnread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2.5 w-[360px] sm:w-[420px] max-w-[92vw] bg-white rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.12),0_4px_16px_rgba(0,0,0,0.06)] border border-slate-100 ring-1 ring-black/5 overflow-hidden z-[9999] animate-in fade-in zoom-in-95 duration-150">
          <div className="px-5 py-3.5 border-b border-slate-100 bg-gradient-to-b from-slate-50/80 to-white flex justify-between items-center">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-blue-50 text-[#115B9D] flex items-center justify-center border border-blue-100">
                <Bell size={15} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-slate-800 text-sm">Notifications</h3>
                  {totalUnread > 0 && (
                    <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-blue-50 text-[#115B9D] border border-blue-100">
                      {totalUnread} new
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-slate-400">Updates & activity</p>
              </div>
            </div>

            {notifications.length > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#115B9D] hover:text-[#0c4475] hover:bg-blue-50/70 px-2.5 py-1.5 rounded-lg transition-colors"
              >
                <CheckCheck size={14} />
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[360px] overflow-y-auto divide-y divide-slate-100">
            {loading && notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <div className="w-7 h-7 border-2 border-slate-200 border-t-[#115B9D] rounded-full animate-spin" />
                <p className="text-xs text-slate-400">Loading notifications...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center mb-3 text-[#115B9D] border border-blue-100">
                  <Inbox size={22} />
                </div>
                <p className="text-sm font-semibold text-slate-800">You're all caught up!</p>
                <p className="text-xs text-slate-400 mt-1 max-w-[220px]">
                  No unread notifications at the moment.
                </p>
              </div>
            ) : (
              <div>
                {notifications.map((notif) => (
                  <NotificationRow
                    key={notif._id}
                    notif={notif}
                    onRead={handleMarkRead}
                    onOpenModal={handleOpenModal}
                    onNavigate={handleNavigate}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
            <span className="text-[11px] text-slate-400 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Live updates active
            </span>
            <button
              onClick={() => {
                setOpen(false);
                navigate("/orders");
              }}
              className="text-[11px] font-semibold text-[#115B9D] hover:underline flex items-center gap-1"
            >
              View documents
              <ArrowRight size={11} />
            </button>
          </div>
        </div>
      )}

      {selectedNotification && (
        <NotificationModal
          notif={selectedNotification}
          onClose={handleCloseModal}
          onMarkRead={handleMarkRead}
        />
      )}
    </div>
  );
}
